import React, { useState, useEffect } from 'react';
import VideoPlayer from '../VideoPlayer';
import ContractViewer from '../ContractViewer';
import { useUser } from '../../utils/UserContext';
import { cssUrl } from '../../utils/cssVersion';
import {
  fetchTraining,
  addTraining,
  fetchSites,
  deleteTraining,
  updateTraining,
  addSite,
  fetchGroupAccess,
} from '../../utils/api';
import SearchInput from '../shared/SearchInput';
import { Toast, LoadingState, EmptyState } from '../shared';
import useFilterData from '../hooks/useFilterData';

const mobileTrainingStyles = cssUrl('mobileTraining.css');

// ─── Access check ────────────────────────────────────────────────
const canViewContent = (item, userEmail, groupAccess) => {
  const group = item[5];
  if (groupAccess && groupAccess[group]) {
    return groupAccess[group].includes(userEmail?.toLowerCase());
  }
  return true;
};

// ─── Badge ───────────────────────────────────────────────────────
const getBadgeLabel = (type) => {
  switch (type) {
    case 'video':       return 'VID';
    case 'pdf':         return 'DOC';
    case 'interactive': return 'APP';
    default:            return '?';
  }
};

// ─── Icons ───────────────────────────────────────────────────────
const ArrowIcon = () => (
  <svg className="mobile-card-arrow" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
    <path d="m9 18 6-6-6-6" />
  </svg>
);

const CloseIcon = () => (
  <span style={{ fontSize: '1.5rem', lineHeight: 1 }}>✕</span>
);

const BackArrowIcon = () => (
  <svg
    width="20" height="20" className="mobile-back-arrow"
    viewBox="0 0 24 24" fill="none" stroke="currentColor"
    strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"
    style={{ width: '20px', height: '20px', flexShrink: 0 }}
  >
    <path d="M19 12H5" />
    <path d="m12 19-7-7 7-7" />
  </svg>
);

// ─── Filter Chips ────────────────────────────────────────────────
const CHIP_OPTIONS = [
  { label: 'Video', value: 'video' },
  { label: 'PDF',   value: 'pdf' },
  { label: 'App',   value: 'interactive' },
];

const FilterChips = ({ searchQuery, setSearchQuery }) => {
  const terms = searchQuery
    .split(',')
    .map((t) => t.trim())
    .filter(Boolean);

  const toggleChip = (value) => {
    if (terms.includes(value)) {
      setSearchQuery(terms.filter((c) => c !== value).join(', '));
    } else {
      setSearchQuery([...terms, value].join(', '));
    }
  };

  return (
    <div className="mobile-chips-bar">
      {CHIP_OPTIONS.map((chip) => (
        <button
          key={chip.value}
          className={`mobile-chip ${terms.includes(chip.value) ? 'mobile-chip-active' : ''}`}
          onClick={() => toggleChip(chip.value)}
        >
          {chip.label}
        </button>
      ))}
      {terms.length > 0 && (
        <button className="mobile-chip mobile-chip-clear" onClick={() => setSearchQuery('')}>
          Clear
        </button>
      )}
    </div>
  );
};

// ─── Training Card (original MobileTraining style) ──────────────
const MobileTrainingCard = ({ item, onClick, isEditMode, onEdit, onDelete }) => {
  const type = item[2];
  const title = item[3];
  const description = item[4];
  const site = item[5];

  return (
    <div className="mobile-training-card" onClick={() => !isEditMode && onClick(item)}>
      <div className={`mobile-card-badge ${type}`}>{getBadgeLabel(type)}</div>
      <div className="mobile-card-content">
        <div className="mobile-card-title">{title}</div>
        <div className="mobile-card-site">{site}</div>
        {description && <div className="mobile-card-description">{description}</div>}
      </div>
      {isEditMode ? (
        <div className="mobile-card-edit-actions">
          <button className="mobile-edit-btn" onClick={(e) => { e.stopPropagation(); onEdit(item); }} aria-label="Edit">✏️</button>
          <button className="mobile-delete-btn" onClick={(e) => { e.stopPropagation(); onDelete(item); }} aria-label="Delete">🗑️</button>
        </div>
      ) : (
        <ArrowIcon />
      )}
    </div>
  );
};

// ─── Add / Edit Content Modal ────────────────────────────────────
const AddContentModal = ({ type, sites, onClose, onSubmit, isSubmitting, editingItem, onSiteAdded }) => {
  const [formData, setFormData] = useState({
    type,
    title: editingItem?.[3] ?? '',
    description: editingItem?.[4] ?? '',
    group: editingItem?.[5] ?? '',
    link: editingItem?.[6] ?? '',
  });
  const [showSuggestions, setShowSuggestions] = useState(false);
  const [localSites, setLocalSites] = useState(sites);
  const [isNewSite, setIsNewSite] = useState(false);
  const [savingStatus, setSavingStatus] = useState(null);

  useEffect(() => { setLocalSites(sites); }, [sites]);

  const filteredSites = localSites.filter((s) =>
    s?.toLowerCase().includes(formData.group.toLowerCase())
  );

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
    if (name === 'group') {
      const trimmed = value.trim().toLowerCase();
      const exists = localSites.some((s) => s?.toLowerCase() === trimmed);
      const hasMatch = localSites.some((s) => s?.toLowerCase().includes(trimmed));
      setIsNewSite(value.trim() && !exists && !hasMatch);
      setSavingStatus(null);
    }
  };

  const handleSiteSelect = (siteName) => {
    setFormData((prev) => ({ ...prev, group: siteName }));
    setShowSuggestions(false);
    setIsNewSite(false);
    setSavingStatus(null);
  };

  const handleGroupBlur = async () => {
    setTimeout(() => setShowSuggestions(false), 200);
    const newSite = formData.group.trim();
    const exists = localSites.some((s) => s?.toLowerCase() === newSite.toLowerCase());
    if (newSite && !exists) {
      try {
        setSavingStatus('saving');
        setIsNewSite(false);
        await addSite(newSite);
        setLocalSites((prev) => [...prev, newSite]);
        if (onSiteAdded) onSiteAdded(newSite);
        setSavingStatus('saved');
        setTimeout(() => setSavingStatus(null), 2000);
      } catch {
        setSavingStatus(null);
        setIsNewSite(true);
      }
    } else {
      setIsNewSite(false);
    }
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    onSubmit(formData, !!editingItem, editingItem?.[0]);
  };

  const typeLabels = {
    video:       { title: editingItem ? 'Update Video' : 'Add Video', icon: '🎬', linkLabel: 'Video URL' },
    pdf:         { title: editingItem ? 'Update PDF' : 'Add PDF',     icon: '📄', linkLabel: 'PDF URL' },
    interactive: { title: editingItem ? 'Update App' : 'Add App',     icon: '🎮', linkLabel: 'App URL' },
  };
  const info = typeLabels[type] || { title: 'Add Content', icon: '📝', linkLabel: 'URL' };

  return (
    <div className="mobile-form-modal-overlay" onClick={onClose}>
      <div className="mobile-modal-content" onClick={(e) => e.stopPropagation()}>
        <div className="mobile-modal-handle" />
        <div className="mobile-modal-header">
          <div className="mobile-modal-title-group">
            <h3 className="mobile-modal-title">{info.icon} {info.title}</h3>
          </div>
          <button className="mobile-modal-close" onClick={onClose}><CloseIcon /></button>
        </div>
        <div className="mobile-modal-body">
          <form onSubmit={handleSubmit} className="mobile-form">
            <div className="mobile-form-group">
              <label className="mobile-form-label">Title</label>
              <input type="text" name="title" className="mobile-form-input" placeholder="Enter title..." value={formData.title} onChange={handleInputChange} required maxLength={100} />
            </div>

            <div className="mobile-form-group">
              <label className="mobile-form-label">Description</label>
              <input name="description" className="mobile-form-input" placeholder="Brief description..." value={formData.description} onChange={handleInputChange} maxLength={200} />
            </div>

            <div className="mobile-form-group" style={{ position: 'relative' }}>
              <label className="mobile-form-label">Group (Site)</label>
              {savingStatus === 'saved' && (
                <div className="mobile-site-toast mobile-site-toast-success">✓ New site saved</div>
              )}
              <input type="text" name="group" className="mobile-form-input" placeholder="Select or type a site..." value={formData.group} onChange={handleInputChange} onFocus={() => setShowSuggestions(true)} onBlur={handleGroupBlur} required autoComplete="off" />
              {showSuggestions && filteredSites.length > 0 && (
                <ul className="mobile-site-suggestions">
                  {filteredSites.map((site) => (
                    <li key={site} onMouseDown={() => handleSiteSelect(site)}>{site}</li>
                  ))}
                </ul>
              )}
              {isNewSite && formData.group.trim() && (
                <div className="mobile-site-toast mobile-site-toast-info">➕ Adding "{formData.group.trim()}"</div>
              )}
              {savingStatus === 'saving' && (
                <div className="mobile-site-toast mobile-site-toast-warn">⏳ Saving…</div>
              )}
            </div>

            <div className="mobile-form-group">
              <label className="mobile-form-label">{info.linkLabel}</label>
              <input type="url" name="link" className="mobile-form-input" placeholder="https://..." value={formData.link} onChange={handleInputChange} required />
            </div>

            <div className="mobile-form-actions">
              <button type="button" className="mobile-btn mobile-btn-secondary" onClick={onClose}>Cancel</button>
              <button type="submit" className="mobile-btn mobile-btn-primary" disabled={isSubmitting}>
                {isSubmitting ? 'Saving…' : editingItem ? 'Update' : 'Add'}
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
};

// ─── Viewer Modals ───────────────────────────────────────────────
const VideoViewerModal = ({ item, onClose }) => (
  <div className="mobile-modal-overlay" onClick={onClose}>
    <div className="mobile-modal-content" onClick={(e) => e.stopPropagation()}>
      <div className="mobile-modal-handle" />
      <div className="mobile-modal-header">
        <div className="mobile-modal-title-group">
          <h3 className="mobile-modal-title">{item[3]}</h3>
          <p className="mobile-modal-subtitle">{item[5]}</p>
        </div>
        <button className="mobile-modal-close" onClick={onClose}><CloseIcon /></button>
      </div>
      <div className="mobile-modal-body">
        <div className="mobile-video-container">
          <VideoPlayer url={item[6]} title={item[3]} autoPlay muted />
        </div>
        {item[4] && <div className="mobile-modal-description">{item[4]}</div>}
      </div>
    </div>
  </div>
);

const PDFViewerModal = ({ item, onClose }) => {
  if (!item) return null;
  return (
    <div className="mobile-modal-overlay" onClick={onClose}>
      <div className="mobile-modal-content" onClick={(e) => e.stopPropagation()}>
        <div className="mobile-modal-handle" />
        <div className="mobile-modal-header">
          <div className="mobile-modal-title-group">
            <h3 className="mobile-modal-title">{item[3]}</h3>
            <p className="mobile-modal-subtitle">{item[5]}</p>
          </div>
          <button className="mobile-modal-close" onClick={onClose}><CloseIcon /></button>
        </div>
        <div className="mobile-modal-body">
          <div className="mobile-pdf-container">
            <ContractViewer pdfUrl="/static/pdffilters/SNL_Onboarding_Hub_Help.pdf" onClose={onClose} />
          </div>
        </div>
      </div>
    </div>
  );
};

const InteractiveViewerModal = ({ item, onClose }) => (
  <div className="mobile-modal-overlay" onClick={onClose}>
    <div className="mobile-modal-content" onClick={(e) => e.stopPropagation()}>
      <div className="mobile-modal-handle" />
      <div className="mobile-modal-header">
        <div className="mobile-modal-title-group">
          <h3 className="mobile-modal-title">{item[3]}</h3>
          <p className="mobile-modal-subtitle">{item[5]}</p>
        </div>
        <button className="mobile-modal-close" onClick={onClose}><CloseIcon /></button>
      </div>
      <div className="mobile-modal-body">
        <div className="mobile-interactive-container">
          <iframe
            src={item[6]} title={item[3]}
            allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
            sandbox="allow-forms allow-modals allow-popups allow-presentation allow-same-origin allow-scripts"
            allowFullScreen
          />
        </div>
      </div>
    </div>
  </div>
);

// ═════════════════════════════════════════════════════════════════
// MAIN COMPONENT
// ═════════════════════════════════════════════════════════════════
const MobileTraining = () => {
  const { user } = useUser() || {};
  const email = user?.email || '';

  // ── Editing state ──
  const [isEditMode, setIsEditMode] = useState(false);
  const [isNominee, setIsNominee] = useState(false);
  const [groupAccess, setGroupAccess] = useState({});
  const [sites, setSites] = useState([]);
  const [editingItem, setEditingItem] = useState(null);
  const [selectedFormType, setSelectedFormType] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [viewerItem, setViewerItem] = useState(null);
  const [editToast, setEditToast] = useState(null);
  const [pendingDelete, setPendingDelete] = useState(null);

  // ── Fetch wrapper for useFilterData ──
  // useFilterData expects fetchData({ monthYear }) → { status, [dataKey], total }
  const fetchDataForFilter = async () => {
    const [trainingResponse, sitesResponse, groupAccessResponse] = await Promise.all([
      fetchTraining(),
      fetchSites(),
      fetchGroupAccess(),
    ]);

    setSites(Array.isArray(sitesResponse) ? sitesResponse : []);
    setGroupAccess(groupAccessResponse.groupAccess || {});
    setIsNominee(groupAccessResponse.isNominee || false);

    const rawItems = trainingResponse.training || [];
    const ga = groupAccessResponse.groupAccess || {};
    const accessible = rawItems.filter((item) => canViewContent(item, email, ga));

    return {
      status: 'success',
      training: accessible,
      total: accessible.length,
    };
  };

  // ── useFilterData hook ──
  const {
    filteredItems,
    items,
    totalCount,
    loading,
    searchQuery,
    setSearchQuery,
    toast,
    setToast,
    getData,
  } = useFilterData({
    fetchData: fetchDataForFilter,
    dataKey: 'training',
    searchFields: [2, 3, 4, 5], // type, title, description, group
    currentMonthFilter: 'all',   // no month filtering needed
    stylesheetHref: mobileTrainingStyles,
    chipLogic: 'or',
  });

  // ── Edit handlers ──
  const handleEdit = (item) => {
    setEditingItem(item);
    setSelectedFormType(item[2]);
  };

  const handleDelete = (item) => {
    setPendingDelete(item);
    setEditToast({
      message: `Delete "${item[3]}"?`,
      type: 'warning',
      action: {
        label: 'Delete',
        onClick: () => confirmDelete(item),
      },
    });
  };

  const confirmDelete = async (item) => {
    setEditToast(null);
    setPendingDelete(null);
    try {
      await deleteTraining(item[0]);
      setEditToast({ message: 'Deleted successfully', type: 'success' });
      getData('all');
    } catch (err) {
      console.error('Delete failed:', err);
      setEditToast({ message: 'Failed to delete', type: 'error' });
    }
  };

  const handleAddNew = (type) => {
    setEditingItem(null);
    setSelectedFormType(type);
  };

  const handleFormSubmit = async (formData, isEditable, recordId) => {
    setSubmitting(true);
    try {
      if (isEditable) {
        await updateTraining({
          id: recordId,
          type: formData.type,
          title: formData.title,
          description: formData.description,
          group: formData.group,
          link: formData.link,
        });
      } else {
        await addTraining({
          type: formData.type,
          title: formData.title,
          description: formData.description,
          group: formData.group,
          link: formData.link,
        });
      }
      setSelectedFormType(null);
      setEditingItem(null);
      setEditToast({
        message: isEditable ? 'Updated successfully' : 'Added successfully',
        type: 'success',
      });
      getData('all'); // refresh list
    } catch (err) {
      console.error('Submit error:', err);
      setEditToast({
        message: isEditable ? 'Failed to update' : 'Failed to add',
        type: 'error',
      });
    } finally {
      setSubmitting(false);
    }
  };

  const handleSiteAdded = (newSite) => {
    setSites((prev) => [...prev, newSite]);
  };

  // ── Viewer ──
  const handleCardClick = (item) => {
    if (!isEditMode) setViewerItem(item);
  };

  const renderViewer = () => {
    if (!viewerItem) return null;
    const close = () => setViewerItem(null);
    switch (viewerItem[2]) {
      case 'video':       return <VideoViewerModal item={viewerItem} onClose={close} />;
      case 'pdf':         return <PDFViewerModal item={viewerItem} onClose={close} />;
      case 'interactive': return <InteractiveViewerModal item={viewerItem} onClose={close} />;
      default:            return null;
    }
  };

  // Combine toasts
  const activeToast = editToast || toast;
  const clearToast = () => { setEditToast(null); setToast(null); setPendingDelete(null); };

  return (
    <div className="mobile-training-container pt-4" style={{
          maxHeight: '80dvh',          
          overflowY: 'auto', 
          WebkitOverflowScrolling: 'touch',
        }}
      >
      {/* ── Header ── */}
      <div className="mobile-header">
        
        <h1 className="mobile-title">Training</h1>
      </div>

      {/* ── Search ── */}
      <SearchInput
        value={searchQuery}
        onChange={setSearchQuery}
        placeholder="Search by title or group..."
      />

      {/* ── Edit mode toggle (nominees only) ── */}
      {isNominee && (
        <div className="mobile-edit-toggle-bar">
          <div className="mobile-mode-toggle">
            <span className={`mobile-mode-label ${isEditMode ? 'active' : ''}`}>Edit</span>
            <div
              className={`mobile-toggle-switch ${isEditMode ? '' : 'production'}`}
              onClick={() => {
                setIsEditMode((prev) => !prev);
                setSelectedFormType(null);
                setEditingItem(null);
              }}
              role="switch"
              aria-checked={isEditMode}
            />
            <span className={`mobile-mode-label ${!isEditMode ? 'active' : ''}`}>View</span>
          </div>

          {isEditMode && (
            <div className="mobile-add-buttons">
              <button className="mobile-add-btn" onClick={() => handleAddNew('video')}>+ Video</button>
              <button className="mobile-add-btn" onClick={() => handleAddNew('pdf')}>+ PDF</button>
              <button className="mobile-add-btn" onClick={() => handleAddNew('interactive')}>+ App</button>
            </div>
          )}
        </div>
      )}

      {/* ── Filter chips (replaces bottom bar) ── */}
      <FilterChips searchQuery={searchQuery} setSearchQuery={setSearchQuery} />

      {/* ── Results info ── */}
      <div className="fr-results-bar">
        <div className="fr-results-info">
          <span className="fr-results-title">Training Materials</span>
          <span className="fr-results-count">Total: {totalCount}</span>
        </div>
        {searchQuery && (
          <div className="fr-results-actions">
            <span className="fr-results-search">
              Showing {filteredItems.length} of {items.length}
            </span>
          </div>
        )}
      </div>

      {/* ── Cards list ── */}
      <div className="mobile-cards-container" style={{ flex: 1, minHeight: 0, overflowY: 'auto', WebkitOverflowScrolling: 'touch',  paddingBottom: 'calc(64px + env(safe-area-inset-bottom))', }}>
        {loading ? (
          <LoadingState message="Loading training materials..." />
        ) : filteredItems.length > 0 ? (
          <div className="mobile-cards-list">
            {filteredItems.map((item, idx) => (
              <MobileTrainingCard
                key={item[0] || item[1] || idx}
                item={item}
                onClick={handleCardClick}
                isEditMode={isEditMode}
                onEdit={handleEdit}
                onDelete={handleDelete}
              />
            ))}
          </div>
        ) : (
          <EmptyState
            icon="📚"
            title="No Training Materials"
            description={
              searchQuery
                ? 'Try adjusting your search or filter'
                : 'No training materials available'
            }
          />
        )}
      </div>

      {/* ── Viewer modals ── */}
      {renderViewer()}

      {/* ── Add/Edit modal ── */}
      {selectedFormType && (
        <AddContentModal
          type={selectedFormType}
          sites={sites}
          onClose={() => { setSelectedFormType(null); setEditingItem(null); }}
          onSubmit={handleFormSubmit}
          isSubmitting={submitting}
          editingItem={editingItem}
          onSiteAdded={handleSiteAdded}
        />
      )}

      {/* ── Toast ── */}
      {activeToast && (
        <Toast
          message={activeToast.message}
          type={activeToast.type}
          onClose={clearToast}
          action={activeToast.action}
        />
      )}
    </div>
  );
};

export default MobileTraining;